import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LegalFooter from '../components/LegalFooter';

const EyeIcon = () => (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);
const EyeOffIcon = () => (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
);

const LoginView: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();

    React.useEffect(() => {
        const savedEmail = localStorage.getItem('rememberedEmail');
        if (savedEmail) {
            setEmail(savedEmail);
            setRememberMe(true);
        }
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            await login(email, password, rememberMe);
            if (rememberMe) {
                localStorage.setItem('rememberedEmail', email);
            } else {
                localStorage.removeItem('rememberedEmail');
            }
            window.location.href = '/';
        } catch (err: any) {
            if (err.response) {
                const status = err.response.status;
                const msg = err.response.data?.message || '';
                if (status === 401) setError('Credenziali non valide. Riprova.');
                else if (status === 403) setError('Account non attivo. Controlla la tua email.');
                else if (status === 404) setError('Utente non trovato.');
                else setError(msg || 'Errore imprevisto del server.');
            } else if (err.request) {
                setError('Impossibile contattare il server.');
            } else {
                setError("Errore durante l'accesso.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* ── PANNELLO SINISTRO (brand) ── */}
            <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative flex-col items-center justify-center overflow-hidden bg-slate-900">
                {/* Gradiente sovrapposto */}
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 opacity-90" />

                {/* Cerchi decorativi sfocati */}
                <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-3xl" />

                {/* Griglia di punti decorativa */}
                <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
                        backgroundSize: '32px 32px',
                    }}
                />

                {/* Contenuto brand */}
                <div className="relative z-10 flex flex-col items-center text-center px-12 max-w-lg">
                    <img
                        src="/logo.webp"
                        alt="Ultra Detective"
                        className="w-52 h-auto object-contain mb-10 drop-shadow-2xl"
                    />
                    <h1 className="text-3xl font-bold text-white tracking-tight leading-tight">
                        Portale Operativo
                    </h1>
                    <p className="mt-3 text-slate-400 text-base leading-relaxed">
                        Gestisci mandati, incarichi e risorse da un'unica piattaforma sicura.
                    </p>

                    {/* Badge features */}
                    <div className="mt-10 flex flex-col gap-3 w-full">
                        {[
                            { icon: '🔒', label: 'Accesso protetto e cifrato' },
                            { icon: '📁', label: 'Archivio mandati centralizzato' },
                            { icon: '📍', label: 'Gestione multi-sede integrata' },
                        ].map(({ icon, label }) => (
                            <div key={label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-left">
                                <span className="text-lg">{icon}</span>
                                <span className="text-slate-300 text-sm font-medium">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Anno in basso */}
                <p className="absolute bottom-6 text-slate-600 text-xs z-10">
                    &copy; {new Date().getFullYear()} Ultra Detective
                </p>
            </div>

            {/* ── PANNELLO DESTRO (form) ── */}
            <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 sm:px-10 py-12">
                {/* Logo mobile (visibile solo su schermi piccoli) */}
                <div className="lg:hidden mb-8 flex flex-col items-center">
                    <img src="/logo.webp" alt="Ultra Detective" className="w-36 h-auto object-contain" />
                </div>

                <div className="w-full max-w-sm">
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-slate-900">Bentornato</h2>
                        <p className="mt-1 text-sm text-slate-500">Inserisci le credenziali per accedere</p>
                    </div>

                    {/* Error box */}
                    {error && (
                        <div className="mb-5 flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                            <svg className="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                                Email aziendale
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                autoComplete="email"
                                placeholder="nome@azienda.it"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:bg-white"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:bg-white"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 px-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                </button>
                            </div>
                        </div>

                        {/* Ricordami + Password dimenticata */}
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2.5 cursor-pointer select-none group">
                                <div
                                    onClick={() => setRememberMe(!rememberMe)}
                                    className={`w-9 h-5 rounded-full flex items-center transition-colors duration-200 cursor-pointer ${
                                        rememberMe ? 'bg-blue-600' : 'bg-slate-200'
                                    }`}
                                >
                                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 mx-0.5 ${
                                        rememberMe ? 'translate-x-4' : 'translate-x-0'
                                    }`} />
                                </div>
                                <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                                    Ricordami
                                </span>
                            </label>
                            <a
                                href="/recover-password"
                                className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                            >
                                Password dimenticata?
                            </a>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-3.5 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/20 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-blue-500/30 ${
                                isSubmitting ? 'opacity-75 cursor-wait' : 'active:scale-[0.98]'
                            }`}
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Accesso in corso...
                                </span>
                            ) : (
                                'Accedi al Gestionale'
                            )}
                        </button>
                    </form>

                    <LegalFooter variant="public" className="mt-8 !py-0" />
                </div>
            </div>
        </div>
    );
};

export default LoginView;
